import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** Incremented per variant change so an older (cached, non-abortable) response can't overwrite a newer one */
  #requestToken = 0;

  /** @type {number | undefined} */
  #prefetchTimer;

  /**
   * Prefetched variant responses, keyed by request URL (shared by all pickers).
   * @type {Map<string, { time: number, text: Promise<string> }>}
   */
  static #prefetchCache = new Map();

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('pointerover', this.#showSwatchTooltip);
    this.addEventListener('pointerout', this.#hideSwatchTooltip);
    // Fetch the variant (and preload its images) as soon as the shopper points at / touches / focuses an option
    this.addEventListener('pointerover', this.#schedulePrefetch);
    this.addEventListener('pointerout', this.#cancelPrefetch);
    this.addEventListener('touchstart', this.#schedulePrefetch, { passive: true });
    this.addEventListener('focusin', this.#schedulePrefetch);
    this.#resizeObserver.observe(this);
    requestAnimationFrame(() => this.updateRowsToggle());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    VariantPicker.#swatchTooltip?.removeAttribute('data-visible');
  }

  /** @type {HTMLElement | undefined} */
  static #swatchTooltip;

  /**
   * Shows a tooltip with the hovered swatch's value above the swatch.
   * Rendered in the body so it isn't clipped by the swatch or row-limited wrapper overflow.
   * @param {PointerEvent} event
   */
  #showSwatchTooltip = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label--has-swatch');
    const input = label?.querySelector('input');
    if (!label || !input) return;

    if (!VariantPicker.#swatchTooltip) {
      VariantPicker.#swatchTooltip = document.createElement('div');
      VariantPicker.#swatchTooltip.className = 'variant-swatch-tooltip';
      VariantPicker.#swatchTooltip.setAttribute('aria-hidden', 'true');
      // Popover puts it in the top layer so it also shows above modal dialogs (e.g. quick add)
      VariantPicker.#swatchTooltip.setAttribute('popover', 'manual');
      document.body.append(VariantPicker.#swatchTooltip);
      window.addEventListener(
        'scroll',
        () => VariantPicker.#swatchTooltip?.removeAttribute('data-visible'),
        { passive: true, capture: true }
      );
    }

    const tooltip = VariantPicker.#swatchTooltip;
    const rect = label.getBoundingClientRect();
    tooltip.textContent = input.value;
    tooltip.style.left = `${rect.left + rect.width / 2}px`;
    tooltip.style.top = `${rect.top}px`;
    if (typeof tooltip.showPopover === 'function') {
      // Re-open so it is stacked above any dialog opened since it was last shown
      if (tooltip.matches(':popover-open')) tooltip.hidePopover();
      tooltip.showPopover();
    }
    tooltip.setAttribute('data-visible', '');
  };

  /**
   * Hides the swatch tooltip once the pointer leaves the swatch.
   * @param {PointerEvent} event
   */
  #hideSwatchTooltip = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label--has-swatch');
    if (!label) return;
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

    VariantPicker.#swatchTooltip?.removeAttribute('data-visible');
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // On the product page only the product section is re-rendered (much smaller and faster than the whole page)
    const sectionId = isOnProductPage && !loadsNewProduct && !isOnFeaturedProductSection ? this.#mainSectionId : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, [], sectionId), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string} [sectionId] - Render only this section (Section Rendering API) instead of the whole page.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionId = undefined) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (sectionId) params.push(`section_id=${sectionId}`);

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * ID of the product section this picker lives in, for the Section Rendering API.
   * @returns {string | undefined}
   */
  get #mainSectionId() {
    if (this.dataset.templateProductMatch !== 'true') return undefined;
    if (this.closest('product-card, quick-add-dialog, featured-product-information')) return undefined;

    return this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') || undefined;
  }

  /**
   * @param {Event} event
   */
  #schedulePrefetch = (event) => {
    if (!(event.target instanceof Element)) return;

    const input = event.target.closest('label')?.querySelector('input') ?? event.target.closest('input');
    if (!(input instanceof HTMLInputElement) || input.checked || !this.contains(input)) return;

    clearTimeout(this.#prefetchTimer);
    // Small delay so sweeping the pointer across many swatches doesn't fire a request for each
    this.#prefetchTimer = setTimeout(() => this.#prefetch(input), event.type === 'pointerover' ? 80 : 0);
  };

  #cancelPrefetch = () => {
    clearTimeout(this.#prefetchTimer);
  };

  /**
   * Fetches the response for an option before it is selected and preloads its first images.
   * @param {HTMLInputElement} input
   */
  #prefetch(input) {
    const sectionId = this.#mainSectionId;
    if (!sectionId) return;

    // A different product (combined listing) re-renders the whole page; don't prefetch that
    const productUrl = this.dataset.productUrl?.split('?')[0];
    const connectedUrl = input.dataset.connectedProductUrl?.split('?')[0];
    if (!productUrl || (connectedUrl && connectedUrl !== productUrl)) return;

    const fieldset = input.closest('fieldset');
    const optionValues = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (option) =>
        fieldset && option.closest('fieldset') === fieldset
          ? input.dataset.optionValueId
          : /** @type {HTMLElement} */ (option).dataset.optionValueId
    );
    if (optionValues.some((value) => !value)) return;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValues.join(',')}`);
    params.push(`section_id=${sectionId}`);
    const url = `${connectedUrl || productUrl}?${params.join('&')}`;

    const cache = VariantPicker.#prefetchCache;
    const cached = cache.get(url);
    if (cached && Date.now() - cached.time < 60_000) return;

    const text = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`Prefetch failed: ${response.status}`);
      return response.text();
    });
    text.catch(() => cache.delete(url));
    cache.set(url, { time: Date.now(), text });
    if (cache.size > 30) cache.delete(cache.keys().next().value);

    // Start downloading the new gallery's first images right away (respects srcset/sizes)
    text
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelectorAll('media-gallery img').forEach((img, index) => {
          if (index > 1 || !(img instanceof HTMLImageElement)) return;
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          if (img.srcset) link.setAttribute('imagesrcset', img.srcset);
          if (img.sizes) link.setAttribute('imagesizes', img.sizes);
          link.href = img.src;
          document.head.append(link);
        });
      })
      .catch(() => {});
  }

  /**
   * Returns (and removes) a still-fresh prefetched response for this URL.
   * @param {string} url
   * @returns {Promise<string> | undefined}
   */
  static #takePrefetched(url) {
    const cached = VariantPicker.#prefetchCache.get(url);
    if (!cached) return undefined;
    VariantPicker.#prefetchCache.delete(url);
    return Date.now() - cached.time < 60_000 ? cached.text : undefined;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const token = ++this.#requestToken;

    const textPromise =
      VariantPicker.#takePrefetched(requestUrl) ??
      fetch(requestUrl, { signal: this.#abortController.signal }).then((response) => response.text());

    textPromise
      .then((responseText) => {
        // A newer variant change happened meanwhile
        if (token !== this.#requestToken) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();
    requestAnimationFrame(() => this.updateRowsToggle());

    return newProduct;
  }

  /**
   * Re-checks overflow on all row-limited toggle buttons and shows/hides them accordingly.
   * Needed because inline scripts don't re-execute after morph updates the DOM.
   */
  updateRowsToggle() {
    this.querySelectorAll('.variant-rows-toggle').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const items = btn.previousElementSibling;
      if (items instanceof HTMLElement && items.scrollHeight > items.clientHeight + 1) {
        btn.style.display = '';
      } else {
        btn.style.display = 'none';
      }
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
